import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  agentModulesQuery,
  saveAgentModule,
  saveAgentModuleMenu,
  saveAgentModuleXdr,
  saveAgentProductivitee,
  type DeviceAgentModuleRow,
  type XdrMode,
  type DeviceAgentModulesPage,
} from '../api/devices'
import { ApiError } from '../api/client'
import { toast } from '../ui/notify'
import { Info } from '../ui/Info'
import { Modal } from '../ui/Modal'
import { SettingsCheck, SettingsRadio } from '../ui/SettingsParts'
import { Spinner } from '../ui/Spinner'
import './DeviceAgentModules.css'

/**
 * Fila Modules - overview/devices/others/deviceModuleSettings.php: catalogul
 * (fara ID 6) pe coloanele Module / Agent / Application Menu / Module
 * extensions, si Remote Control pe ultimul rand.
 *
 * O SINGURA COLOANA, "BASIC" (24 septembrie 2026)
 * -----------------------------------------------------------------------------
 * Free si Premium erau doua coloane pentru acelasi lucru - agentul nu le
 * deosebeste, citeste starea modulului ca pe o poarta
 * (`windows-agent/internal/prodtee/push_windows.go:76`). Acum e o bifa. Capul
 * ei s-a numit intai "Agent"; de la a doua trecere din aceeasi zi scrie
 * "Basic", fiindca asta aprinde chiar: nivelul de plecare al modulului, peste
 * care coloana "Module extensions" urca la Extended.
 *
 * SCHIMBAREA E NUMAI ETICHETA. In spate contractul a ramas intreg: aprinderea
 * scrie 'premium', stingerea scrie 'disabled', pe aceleasi capete si cu
 * aceleasi campuri, iar bifa poarta mai departe `name="premium_module_<id>"`
 * si clasele `iCheckModulePremium iCheckM`. La fel si clasele de coloana
 * (`dam-c-agent`), ca sa nu se rescrie foaia si probele pentru un cuvant.
 *
 * NIVELUL NU MAI STA IN COLOANA ASTA. Pana la trecerea asta, randul
 * Productivitee purta aici, langa bifa, un buton "Basic / Extended" care
 * redeschidea fereastra de acord. A plecat: aprinderea bifei deschide
 * fereastra DIRECT pe Basic, iar Extended se cere numai din bifa lui din
 * coloana "Module extensions". Un singur loc pentru un singur lucru.
 *
 * DESENUL, de pe 24 septembrie 2026
 * -----------------------------------------------------------------------------
 * Pana atunci markupul era cel din macheta CodeIgniter, cu tot cu culorile ei
 * scrise inline: capul galben (`#FFCF6E`) si un bloc gri-inchis (`#656565`) pe
 * toata coloana Free. In portalul vechi gri-ul ala era hartia pe care iCheck
 * desena patratul; v4 nu incarca iCheck, deci ramanea doar blocul. Culorile au
 * iesit din componenta si au intrat in `DeviceAgentModules.css`, care vine cu
 * ecranul oriunde e pus - fila nu mai depinde de ce incarca parintele.
 *
 * BIFE, NU COMUTATOARE (24 septembrie 2026, a doua trecere)
 * -----------------------------------------------------------------------------
 * Prima trecere a schimbat bifele in comutatoare (`SettingsSwitch`). Cererea
 * era alta, si s-a spus raspicat: "sa ramana checkboxuri, dar stilizate". Deci
 * aici sta `SettingsCheck` - bifa casei din `SettingsParts.tsx`, aceeasi pe
 * cincisprezece ecrane, cu `accent-color: var(--od-ink)` si 15px, valorile
 * consolei (`od_console.css`, sectiunea 5). `SettingsSwitch` s-a nascut in
 * livrarea aia si n-a fost folosit nicaieri altundeva, deci a plecat cu totul
 * din `SettingsParts.tsx` si din foaia lui.
 *
 * Comportamentul nu s-a schimbat: fiecare bifa scrie pe loc pe server.
 * `name`, `value`, `data-*` si clasele `iCheck*` sunt exact cele de dinainte -
 * probele si orice altceva le cauta dupa ele.
 *
 * Productivitee (coloana Compliance) e tratat aparte: la aprindere se cere
 * acordul (acoperire legala), pe nivelul Basic. Compliance ramane on/off;
 * nivelul si acordul se scriu separat (server).
 */

export const PRODUCTIVITEE_LEVEL = { basic: 1, extended: 2 } as const

/**
 * Ce scrie in baloanele de langa fiecare coloana.
 *
 * CE FACE AGENTUL, NU O FORMULARE GENERALA (24 septembrie 2026)
 * -----------------------------------------------------------------------------
 * Prima formulare spunea mecanica PORTALULUI - ce bifa pe cine blocheaza, ce se
 * stinge cand stingi altceva. Adevarat, dar omul care deschide fila intreaba
 * altceva: ce se schimba PE MASINA. Asa ca randurile de aici au fost recitite
 * din codul AGENTULUI, si scriu ce face el diferit.
 *
 * Ce s-a gasit acolo e neplacut si sta scris ca atare, fiindca o diferenta
 * inventata ar fi mai rea: AGENTUL NU DEOSEBESTE Free de Premium. Go-ul citeste
 * starea unui singur modul - Productivitee - si o citeste ca pe o poarta:
 * `windows-agent/internal/prodtee/push_windows.go:76` (`if status <= 0 { ...
 * return nil }`), peste valoarea adusa de
 * `windows-agent/internal/api/extra.go:137-171` ("> 0 = modul activ pe
 * aparat"). Valorile pe care le scrie portalul - 3 pentru Free, 1 pentru
 * Premium (`AgentModules::statusValue`) - pleaca spre agent ca atare
 * (ph4.api: `app/Services/ModulesSettings/Settings.php:187-377`), si toate trec
 * de acelasi `> 0`. Restul modulelor nici nu-si citesc starea in agent: o
 * cautare dupa `moduleStatus` in `windows-agent/` da numai productivitee.
 *
 * Singurul nivel care CHIAR schimba ce face masina e nivelul de colectare al
 * lui Productivitee (Basic/Extended) - vezi `PRODUCTIVITEE_LEVEL_HELP`.
 */
export const LEVEL_HELP = {
  /*
   * O SINGURA COLOANA, 24 septembrie 2026
   * ---------------------------------------------------------------------------
   * Free si Premium au fost doua coloane pana aici, si erau doua coloane pentru
   * acelasi lucru: "dispare free si premium si se denumeste Agent (in spate
   * poate ramane premium) ca oricum fac acelasi lucru".
   *
   * Capul ei scrie de la a doua trecere "Basic", nu "Agent" - "coloana agent
   * cred ca ar trebui redenumita in Basic". Cheia de aici a ramas `agent`,
   * fiindca ea numeste CE SE SCRIE (starea modulului catre agent), nu cuvantul
   * de pe ecran; si textul de mai jos e neschimbat, fiindca el descrie exact
   * bifa aia, care n-a capatat si n-a pierdut nimic la redenumire.
   *
   * In spate NU s-a schimbat nimic: aprinderea scrie exact ce scria Premium
   * ('premium', adica `AgentModules::statusValue` -> 1), stingerea scrie exact
   * ce scria stingerea ('disabled' -> 0, si `AgentSettings::saveModule` cheama
   * pe ramura aia `setMenu(..., false)`). Capetele, campurile si ce pleaca spre
   * agent au ramas litera cu litera.
   *
   * Agent: valoarea ajunge ca `moduleStatus` si e citita DOAR ca poarta -
   * `windows-agent/internal/prodtee/push_windows.go:76` (`if status <= 0`),
   * peste `windows-agent/internal/api/extra.go:122`. De aia cele doua coloane
   * nu se puteau deosebi pe masina.
   */
  agent:
    'Turns the module on for this machine. Switching it off stops the module here and also removes it from the machine’s Application Menu.',
  /*
   * Bitul din `Menus` (`AgentModules::MENU_BITS`), trimis ca `moduleMenu`
   * (ph4.api: `app/Services/ModulesSettings/Settings.php:188-377`). In agentul
   * Go nu-l citeste nimeni - o cautare dupa `moduleMenu` in `windows-agent/`,
   * `linux-agent/` si `mac-agent/` nu da niciun rezultat; il foloseste programul
   * de pe statie, cel care deseneaza meniul.
   */
  menu:
    'Lists the module in the agent’s own menu on the machine, the one the person at the keyboard opens. It changes what that menu offers, not what is collected. It can only be switched on while the module is on, and switching the module off clears it.',
  /*
   * `AgentSettings::modules`: `extended` e diferit de null NUMAI pentru modulul
   * 3 (Proactivitee / AssetHealth). Restul randurilor au celula goala.
   */
  extensions: 'Extra settings that belong to a single module.',
  /*
   * `AgentModules::statusValue`: 'enabled_extended' scrie AssetHealth = 4,
   * 'disabled_extended' scrie 1 (adica Premium). Lista de aparate din Network
   * Monitoring cere exact 4 - `application/models/network_monitoring/Table_data.php`
   * ("AND it_echipamente.AssetHealth = 4") si
   * `v4/api/src/Proactivitee/DbalDeviceTableSource.php`. Cei 4 pleaca si spre
   * agent (`Settings.php:243`, `proactivitee.moduleStatus`), dar acolo nu-i
   * citeste nimeni: agentul Go nu se uita deloc la starea lui Proactivitee.
   */
  extendedMonitoring:
    'Puts this machine on Proactivitee’s Network Monitoring list - only machines with Extended monitoring on are listed there. Switching it off leaves Proactivitee on.',
  /*
   * XDR pe aparat (Roxana, 2 octombrie 2026): `it_edr_settings`, randul
   * aparatului. Agentul de Windows citeste profilul EDR la urmatoarea
   * reimprospatare; ph4.api trimite evenimentele spre Wazuh (`wazuh_forward`),
   * iar ce iese inapoi se vede in Securitee > Detections. Bifa stinsa scrie 0 pe
   * aparat, deci il tine oprit chiar daca firma are XDR pornit.
   */
  xdr:
    'XDR on this device: the agent collects its security events (Windows logs, Sysmon, processes, network connections) for threat detection. Follow (default): the device follows its group; with no group, or a group that inherits, it follows the company. Always on: on, whatever its group or company says. Always off: off, whatever its group or company says - use it to stop XDR on this device at once. Windows only.',
  /*
   * `it_echipamente.remote_control`, propria lui coloana - nu e in catalogul de
   * module si n-are nici Free, nici Premium, nici bit de meniu. Lista
   * aparatelor pe care se poate deschide o sesiune dintr-un tichet cere exact
   * 1: `application/models/easeedesk/Ticket_affected_devices::getRemoteAffectedDevices`
   * si `v4/api/src/Easeedesk/Ticket/DbalTicketPanelsSource.php` ("AND
   * e.remote_control = 1").
   */
  remoteControl:
    'Not a module. It decides one thing in the portal: with it off, this machine is not offered for a remote session from a ticket.',
} as const

/**
 * Nivelul de colectare al lui Productivitee - SINGURUL loc din fila asta in
 * care alegerea schimba chiar ce face masina. Fiecare propozitie de mai jos are
 * un rand de cod in dreptul ei, in agentul de Windows.
 *
 * Doua lucruri care NU se scriu in balon, ca sa nu-l umple, dar care trebuie
 * stiute: (1) nivelul e al AGENTULUI DE WINDOWS; `linux-agent`, `mac-agent` si
 * `winserver-agent` n-au deloc pachetul `prodtee`, nu cer niciodata
 * `settings/modules_settings` si nu cunosc Basic/Extended. (2) nivelul sta in
 * ALT camp si in ALT tabel decat Free/Premium - `it_productivitee_device_settings.level`,
 * nu `it_echipamente.Compliance`.
 *
 * NU e acelasi text cu `PRODUCTIVITEE_DRAFT`: ala e formularea de ACORD, scrisa
 * ca o diferenta fata de Basic ("Extended also records...") si legata de o
 * versiune inregistrata in API. Citita singura, langa o bifa care scrie
 * "Extended", nu explica nimic - si asta era "i"-ul care nu mergea.
 *
 * Pe ecran se arata acum NUMAI `extended`, pe "i"-ul bifei de Extended: de cand
 * butonul de nivel a iesit din coloana Basic, nu mai exista niciun loc care sa
 * spuna "esti pe Basic". `basic` ramane aici fiindca e jumatatea care da
 * inteles celeilalte - "i"-ul de Extended se citeste ca o DIFERENTA fata de ea
 * - si fiindca proba tine cele doua texte deosebite cuvant cu cuvant.
 */
export const PRODUCTIVITEE_LEVEL_HELP = {
  /*
   * `windows-agent/internal/prodtee/config.go:56` - `LevelBasic = 1 // fara
   * titlu: doar ca aplicatia X a fost in fata`; `config.go:67-72`
   * (`TitlesForLevel`) intoarce "off" pentru orice nu e 2;
   * `browser_windows.go:86-87` (`case "off": return ""`);
   * `helper_windows.go:741-748` - garantia auditabila a lui Basic: pe "off" nu
   * se cheama DELOC `GetWindowText`, titlul ramane "";
   * `helper_windows.go:469-476` si `rescopeNameHook` (`helper_windows.go:517-527`)
   * - hook-ul de nume nu se instaleaza pe Basic, deci blocarea de SITE-uri nu
   * merge; blocarea de APLICATII merge, pe exe, din `handleForeground`.
   */
  basic:
    'Basic: the machine reports only which application was in front and for how long, plus idle time. The agent never asks Windows for the window title - it does not call the title API at all - so no email subject, no document name and no website name is ever read. Application blocking still works, matched on the program file; website blocking does not, because a site is recognised from the window title.',
  /*
   * `config.go:57` - `LevelExtended = 2 // titlu intreg, inclusiv site-ul din
   * browser`; `config.go:68-69` -> "full"; `browser_windows.go:88-98` - pe
   * "full" titlul se curata, si pentru browser se pastreaza NUMAI site-ul
   * (`siteFromTitle`), nu tot titlul paginii ("interogarea de cautare (...)
   * sunt continut sensibil"); `helper_windows.go:517-527` - hook-ul de nume se
   * pune numai pe "full", deci abia aici se vede un site nou intr-un tab.
   */
  extended:
    'Extended: the agent also reads the window title of the application in front. For a normal application that is the whole title - the document, the message, the sheet. For a browser it keeps only the site name ("github.com"), worked out from the window title and never from the address bar, so the search terms and page names in it are dropped on the machine before anything is sent. Extended is also the only level on which website blocking works; on Basic only applications can be blocked.',
} as const

/**
 * Textele de disclaimer si `disclaimerVersion` au perechea in API
 * (`App\Devices\Agent\ProductiviteeConsent`), cu care se inregistreaza acordul;
 * cand se schimba formularea aici, se schimba si versiunea in amandoua.
 */
export const PRODUCTIVITEE_DRAFT = {
  disclaimerVersion: '2026-09-24',
  basic:
    'Only basic info from the focused application: which app it is and for how long, plus the start and end of the working day and idle time. The window title is never read - no email subjects, document names, page titles or website names are collected.',
  /*
   * ACORDUL NU CERE MAI MULT DECAT SE COLECTEAZA, 24 septembrie 2026.
   *
   * Pana aici scria "including the browser page title (a best-effort website
   * name)" - adica omul semna pentru titlul paginii. Agentul nu-l trimite
   * niciodata: pe "full", `browser_windows.go:88-98` pastreaza NUMAI numele
   * sitului (`siteFromTitle`), restul titlului e aruncat pe masina, inainte de
   * orice trimitere. Un text de acord mai larg decat colectarea reala e cel mai
   * scump fel de greseala de aici, si de-aia s-a urcat si `disclaimerVersion`:
   * acordurile deja scrise rămân pe versiunea lor.
   */
  extended:
    'Extended also records the window title of the focused application. For a browser only the site name is kept (for example "github.com"), worked out from the window title - never the address bar, and never the page title. Window titles can contain personal data - email subjects, document names, chat participant names.',
  enableNotice:
    'By enabling Productivitee on this device you confirm that the people who use it have been informed and that your organization has a lawful basis to monitor work activity, in accordance with applicable law and your internal policies.',
  extendedNotice:
    'Extended additionally collects window titles, which may contain personal data. You confirm you have a lawful basis and have given appropriate notice for this additional collection.',
} as const

/**
 * "i"-ul de langa un nivel. Balonul e cel al portalului - `data-toggle`,
 * `data-placement`, `title` - pus de `src/ui/tooltips.ts` (Bootstrap 3 fara
 * jQuery), nu un al doilea fel de a explica ceva. `role="img"` cu `aria-label`
 * fiindca `tooltips.ts` goleste `title` la prima trecere cu mausul, si fara el
 * textul ar disparea pentru cititorul de ecran.
 */
/** Starea scurta din tabel. */
function xdrShort(row: DeviceAgentModuleRow): string {
  if (row.xdr === 'on') return 'Always on'
  if (row.xdr === 'off') return 'Always off'
  return row.xdr_state ? `Follow · ${xdrStateText(row.xdr_state)}` : 'Follow'
}

/** Follow: ce primeste aparatul de la grup / firma. */
export function xdrStateText(state: NonNullable<DeviceAgentModuleRow['xdr_state']>): string {
  if (state.from === 'group') return `${state.on ? 'on' : 'off'}, from group "${state.group ?? ''}"`
  if (state.on) return 'on, from the company'
  return state.group ? `off: group "${state.group}" inherits, and XDR is off for the company` : 'off: no group, and XDR is off for the company'
}

/** De ce Follow da "oprit", fara "off" in fata. */
function followOffReason(state: NonNullable<DeviceAgentModuleRow['xdr_state']>): string {
  if (state.from === 'group') return `group "${state.group ?? ''}" has XDR off`
  return state.group ? `group "${state.group}" inherits, and XDR is off for the company` : 'no group, and XDR is off for the company'
}

/* "i"-ul s-a mutat in `ui/Info.tsx` (il folosesc si nivelurile de acces);
   ramane exportat si de aici, pentru cine il lua de aici. */
export { Info }

export function DeviceAgentModules({ id }: { id: number }) {
  const page = useQuery(agentModulesQuery(id))
  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />
  return <Table page={page.data} />
}

function Table({ page }: { page: DeviceAgentModulesPage }) {
  const query = useQueryClient()
  /* Fereastra de acord poarta si NIVELUL cu care se deschide: din bifa
     coloanei Basic se deschide pe Basic, din bifa Extended a coloanei de
     extensii se deschide direct pe Extended. */
  const [dialog, setDialog] = useState<{ row: DeviceAgentModuleRow; level: number } | null>(null)
  const refresh = async () => {
    await query.invalidateQueries({ queryKey: ['device', page.device.id, 'agent-settings', 'modules'] })
  }
  const write = async (status: string, module = 0) => {
    await saveAgentModule(page.device.id, module, status)
    toast('Module saved!', 'Success')
    await refresh()
  }
  const [xdrDialog, setXdrDialog] = useState<DeviceAgentModuleRow | null>(null)
  const writeXdr = async (mode: XdrMode) => {
    try {
      await saveAgentModuleXdr(page.device.id, mode)
      toast('Module saved!', 'Success')
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'Something went wrong. Please try again.', 'Error', 'error')
    }
    // si dupa un refuz: bifa revine la ce e pe server
    await refresh()
  }
  const writeMenu = async (status: string, module: number) => {
    await saveAgentModuleMenu(page.device.id, module, status)
    toast('Module saved!', 'Success')
    await refresh()
  }
  /* Productivitee: aprinderea deschide fereastra de acord, DIRECT PE BASIC -
     "e considerat direct basic". Pana la trecerea asta pornea de la nivelul
     stocat (`row.level`), dar nivelul se cerea si de la butonul de langa bifa,
     iar butonul a plecat: bifa asta aprinde modulul, si un modul se aprinde de
     jos. Urcarea la Extended se cere din bifa lui, in coloana de extensii.
     Stingerea merge direct pe capul lui, fara acord. */
  const productivitee = async (row: DeviceAgentModuleRow, on: boolean) => {
    if (on) {
      setDialog({ row, level: PRODUCTIVITEE_LEVEL.basic })
      return
    }
    await saveAgentProductivitee(page.device.id, {
      module: row.id,
      status: 'disabled',
      level: row.level ?? PRODUCTIVITEE_LEVEL.basic,
      consent: false,
      consentExtended: false,
    })
    toast('Module saved!', 'Success')
    await refresh()
  }
  /* Bifa Extended din coloana de extensii. Aprinderea deschide ACEEASI
     fereastra, pusa pe Extended, deci acordul in plus se cere ca pana acum.
     Stingerea trimite exact ce trimite fereastra cand se salveaza Basic -
     acelasi cap, aceleasi campuri, alt nivel. */
  const productiviteeLevel = async (row: DeviceAgentModuleRow, extended: boolean) => {
    if (extended) {
      setDialog({ row, level: PRODUCTIVITEE_LEVEL.extended })
      return
    }
    await saveAgentProductivitee(page.device.id, {
      module: row.id,
      status: 'premium',
      level: PRODUCTIVITEE_LEVEL.basic,
      consent: true,
      consentExtended: false,
    })
    toast('Module saved!', 'Success')
    await refresh()
  }

  if (!page.exists) {
    return (
      <>
        <div className="notifications"></div>
        <div className="note note-danger">
          <i className="fas fa-exclamation-circle"></i> This device no longer exists.
        </div>
      </>
    )
  }

  return (
    <>
      <div className="notifications"></div>
      {page.catalog.length === 0 ? (
        <div className="note note-warning">
          <i className="fas fa-exclamation-triangle"></i> No agent module is enabled for this company. Turn
          modules on in Settings &gt; Company Modules.
        </div>
      ) : null}
      <table className="dam-tbl">
        <thead>
          <tr>
            <th className="a-left dam-c-name">Module</th>
            {/* "Basic", nu "Agent" - vezi nota de la `LEVEL_HELP.agent`. Clasa
                coloanei ramane `dam-c-agent`: s-a schimbat cuvantul, nu ce face
                sau ce scrie bifa. */}
            <th className="dam-c-agent">
              Basic
              <Info what="Basic" text={LEVEL_HELP.agent} />
            </th>
            <th className="dam-c-menu">
              Application Menu
              <Info what="Application Menu" text={LEVEL_HELP.menu} />
            </th>
            <th className="dam-c-ext">
              Module extensions
              <Info what="Module extensions" text={LEVEL_HELP.extensions} />
            </th>
          </tr>
        </thead>
        <tbody>
          {page.catalog.map((row) =>
            row.productivitee ? (
              <ProductiviteeRow
                key={row.id}
                deviceId={page.device.id}
                row={row}
                onToggle={productivitee}
                onLevel={productiviteeLevel}
                onMenu={writeMenu}
              />
            ) : (
              <ModuleRow key={row.id} deviceId={page.device.id} row={row} onWrite={write} onMenu={writeMenu} onXdr={setXdrDialog} xdrEditable={page.can_edit_xdr !== false} />
            ),
          )}
          {/* Remote Control nu e un modul: n-are Free, n-are Premium, n-are
              meniu. De pe 24 septembrie 2026 randul lui sta la SFARSITUL
              tabelului, nu lipit sub capul de coloane, unde se citea ca si cum
              ar fi fost o parte a antetului. */}
          <tr className="dam-rc">
            <td className="a-left dam-name">Remote Control</td>
            <td className="dam-rc-cell" colSpan={3}>
              <SettingsCheck
                name="remote_control"
                data-device={page.device.id}
                className="iCheckM iCheckRemoteControl"
                value="1"
                checked={page.remoteControl}
                onChange={(on) => void write(on ? 'enabled_remote_control' : 'disabled_remote_control')}
                label={<span className="dam-rc-note">not a module - on/off only</span>}
              />
              <Info what="Remote Control" text={LEVEL_HELP.remoteControl} />
            </td>
          </tr>
        </tbody>
      </table>
      {xdrDialog !== null ? (
        <XdrDialog
          row={xdrDialog}
          onClose={() => setXdrDialog(null)}
          onSave={async (mode) => {
            await writeXdr(mode)
            setXdrDialog(null)
          }}
        />
      ) : null}
      {dialog !== null ? (
        <ProductiviteeDialog
          deviceId={page.device.id}
          row={dialog.row}
          initialLevel={dialog.level}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null)
            toast('Module saved!', 'Success')
            await refresh()
          }}
        />
      ) : null}
    </>
  )
}

function ModuleRow({
  deviceId,
  row,
  onWrite,
  onMenu,
  onXdr,
  xdrEditable,
}: {
  deviceId: number
  row: DeviceAgentModuleRow
  onWrite(status: string, module: number): Promise<void>
  onMenu(status: string, module: number): Promise<void>
  onXdr(row: DeviceAgentModuleRow): void
  xdrEditable: boolean
}) {
  return (
    <tr>
      <td scope="row" className="a-left dam-name">
        {row.name}
      </td>
      {/* O bifa, nu doua. Numele, clasa si `value` sunt CELE DE LA PREMIUM, si
          la fel sunt si starile scrise: 'premium' la aprindere, 'disabled' la
          stingere - exact ce scria coloana Premium. Coloana Free a disparut
          numai de pe ecran; ce pleaca spre server si spre agent e neschimbat.
          `row.free` ramane citit, fiindca un modul aprins din portalul vechi
          la nivelul Free vine cu `free: true, premium: false` si trebuie sa se
          vada tot aprins. */}
      <td className="dam-c-agent">
        <SettingsCheck
          aria-label={`${row.name} - agent`}
          name={`premium_module_${row.id}`}
          data-device={deviceId}
          data-module={row.id}
          className="iCheckModulePremium iCheckM"
          value={row.id}
          checked={row.premium || row.free}
          onChange={(on) => void onWrite(on ? 'premium' : 'disabled', row.id)}
        />
      </td>
      <td className="dam-c-menu">
        <SettingsCheck
          aria-label={`${row.name} - menu`}
          name={`menu_module_${row.id}`}
          data-device={deviceId}
          data-module={row.id}
          className="iCheckModuleMenu iCheckM"
          value={row.id}
          checked={row.menu}
          disabled={row.menuDisabled}
          onChange={(on) => void onMenu(on ? 'enabled_menu' : 'disabled_menu', row.id)}
        />
      </td>
      <td className="dam-c-ext">
        {row.extended !== null ? (
          <>
            <SettingsCheck
              name="extended_monitoring"
              data-device={deviceId}
              data-module={row.id}
              className="iCheckModuleExtended"
              value="1"
              checked={row.extended}
              onChange={(on) => void onWrite(on ? 'enabled_extended' : 'disabled_extended', row.id)}
              label="Extended monitoring"
            />
            <Info what="Extended monitoring" text={LEVEL_HELP.extendedMonitoring} />
          </>
        ) : null}
        {row.xdr !== null ? (
          <>
            {/* bifa arata daca XDR merge acum; clicul deschide fereastra, nu scrie */}
            <SettingsCheck
              name="xdr"
              data-device={deviceId}
              data-module={row.id}
              className="iCheckModuleXdr"
              value="1"
              checked={row.xdr_state?.on === true}
              disabled={!xdrEditable}
              title={xdrEditable ? undefined : 'You need the right to edit XDR settings.'}
              onChange={() => onXdr(row)}
              label="XDR"
            />
            <Info what="XDR" text={LEVEL_HELP.xdr} />
            {row.xdr_state ? <span className="font-grey-cascade xdr-state" style={{ marginLeft: 6 }}>{xdrShort(row)}</span> : null}
          </>
        ) : null}
      </td>
    </tr>
  )
}

/**
 * Randul lui Productivitee, dupa a doua trecere din 24 septembrie 2026:
 *
 * - in coloana **Basic**: DOAR bifa on/off. Aprinderea ei deschide fereastra de
 *   acord pusa pe Basic; stingerea scrie 'disabled', ca la orice alt modul.
 * - in coloana **Module extensions**: o bifa pentru **Extended**, care deschide
 *   aceeasi fereastra de acord, pusa pe Extended, si care isi poarta "i"-ul.
 *
 * CE A PLECAT, SI DE CE: langa bifa din coloana Basic statea un buton
 * "Basic / Extended" cu creion, care redeschidea aceeasi fereastra. Erau doua
 * cai catre acelasi lucru, iar cea din coloana Basic il lasa pe om sa aleaga
 * Extended dintr-o coloana care nu e a lui - "scoate butonul din coloana
 * Agent". Acum nivelul se cere dintr-un singur loc: bifa Extended.
 *
 * Nivelul e singurul loc din fila in care alegerea schimba chiar ce face masina
 * (`PRODUCTIVITEE_LEVEL_HELP`), deci "i"-ul cu textul lui sta pe bifa Extended.
 */
function ProductiviteeRow({
  deviceId,
  row,
  onToggle,
  onLevel,
  onMenu,
}: {
  deviceId: number
  row: DeviceAgentModuleRow
  onToggle(row: DeviceAgentModuleRow, on: boolean): Promise<void>
  onLevel(row: DeviceAgentModuleRow, extended: boolean): Promise<void>
  onMenu(status: string, module: number): Promise<void>
}) {
  const on = row.free
  const extended = (row.level ?? PRODUCTIVITEE_LEVEL.basic) === PRODUCTIVITEE_LEVEL.extended
  return (
    <tr>
      <td scope="row" className="a-left dam-name">
        {row.name}
      </td>
      <td className="dam-c-agent">
        <SettingsCheck
          aria-label={`${row.name} - enabled`}
          name={`premium_module_${row.id}`}
          data-device={deviceId}
          data-module={row.id}
          className="iCheckModulePremium iCheckM"
          value={row.id}
          checked={on}
          onChange={(next) => void onToggle(row, next)}
        />
      </td>
      <td className="dam-c-menu">
        <SettingsCheck
          aria-label={`${row.name} - menu`}
          name={`menu_module_${row.id}`}
          data-device={deviceId}
          data-module={row.id}
          className="iCheckModuleMenu iCheckM"
          value={row.id}
          checked={row.menu}
          disabled={row.menuDisabled}
          onChange={(next) => void onMenu(next ? 'enabled_menu' : 'disabled_menu', row.id)}
        />
      </td>
      {/* Extensia lui Productivitee: bifa de Extended, cu fereastra lui de
          acord. Se vede numai cat timp modulul e pornit - fara modul n-are ce
          extinde.

          "I"-UL DE LANGA EA, 24 septembrie 2026: "pe bifele de extended nu se
          deschide info-ul". Bifa asta s-a nascut cu o livrare mai devreme in
          aceeasi zi si s-a nascut FARA `<Info>` - n-avea ce sa se deschida,
          nu era nimic acoperit si nimic stins din foaie. Textul e cel al
          nivelului, `PRODUCTIVITEE_LEVEL_HELP.extended`: ce face AGENTUL pe
          masina la Extended, ca diferenta fata de Basic - nu formularea de
          acord din fereastra, care e un disclaimer juridic si citita singura
          nu explica nimic. */}
      <td className="dam-c-ext">
        {on ? (
          <>
            <SettingsCheck
              aria-label={`${row.name} - extended`}
              name={`productivitee_extended_${row.id}`}
              data-device={deviceId}
              data-module={row.id}
              className="iCheckProductiviteeExtended"
              value="1"
              checked={extended}
              onChange={(next) => void onLevel(row, next)}
              label="Extended"
            />
            <Info what="Extended" text={PRODUCTIVITEE_LEVEL_HELP.extended} />
          </>
        ) : null}
      </td>
    </tr>
  )
}

/**
 * Fereastra de aprindere Productivitee: nivelul (Basic / Extended) cu explicatii,
 * disclaimerul de acord (obligatoriu) si, la Extended, disclaimerul lui in plus
 * (tot obligatoriu). Fara bifele cerute nu se poate salva.
 */
const XDR_CHOICES: { mode: XdrMode; label: string; text: string }[] = [
  { mode: 'follow', label: 'Follow group / company (recommended)', text: 'The device follows its group; with no group, or a group that inherits, it follows the company.' },
  { mode: 'on', label: 'Always on', text: 'XDR stays on for this device, whatever its group or company says.' },
  { mode: 'off', label: 'Always off', text: 'XDR stays off for this device, whatever its group or company says. Use it to stop XDR on this device at once.' },
]

/** Fereastra XDR a aparatului, ca la Productivitee: alegerea se face aici, nu in tabel. */
function XdrDialog({ row, onClose, onSave }: { row: DeviceAgentModuleRow; onClose(): void; onSave(mode: XdrMode): Promise<void> }) {
  const current = row.xdr ?? 'follow'
  return (
    <XdrModeDialog
      current={current}
      follow={row.xdr_follow ?? (current === 'follow' ? (row.xdr_state ?? null) : null)}
      onClose={onClose}
      onSave={onSave}
    />
  )
}

type XdrFollow = NonNullable<DeviceAgentModuleRow['xdr_state']>

/**
 * Fereastra XDR, impartita cu fila Modules din panoul de selectie multipla
 * (`inventoree/MultiModulesTab.tsx`). `current: null` = nu se stie starea de
 * acum (douazeci de masini, douazeci de stari): nu se scrie "current" pe
 * nicio alegere si Save nu asteapta o schimbare.
 */
export function XdrModeDialog({
  current,
  follow,
  title = 'XDR on this device',
  onClose,
  onSave,
}: {
  current: XdrMode | null
  follow: XdrFollow | null
  title?: string
  onClose(): void
  onSave(mode: XdrMode): Promise<void>
}) {
  const [mode, setMode] = useState<XdrMode>(current ?? 'follow')
  const [saving, setSaving] = useState(false)

  return (
    <Modal
      id="xdr-mode"
      open
      onClose={onClose}
      title={title}
      labelledBy="xdrModeLabel"
      staticBackdrop
      portal
      footer={
        <>
          {mode === current && !saving ? (
            <span className="xdr-same" style={{ marginRight: 10, color: '#98a1aa', fontSize: 12 }}>
              This is already the current setting.
            </span>
          ) : null}
          <button type="button" className="btn btn-default" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || mode === current}
            onClick={() => {
              setSaving(true)
              void onSave(mode).finally(() => setSaving(false))
            }}
          >
            Save
          </button>
        </>
      }
    >
      {XDR_CHOICES.map((c) => (
        <div key={c.mode} style={{ marginBottom: 12 }}>
          <SettingsRadio
            name="xdr_mode"
            value={c.mode}
            checked={mode === c.mode}
            onChange={(on) => on && setMode(c.mode)}
            label={
              <>
                <strong>{c.label}</strong>
                {c.mode === current ? (
                  <span className="label label-default xdr-current" style={{ marginLeft: 6 }}>
                    current
                  </span>
                ) : null}
              </>
            }
          />
          <div style={{ marginLeft: 26, color: '#98a1aa', fontSize: 13 }}>
            {c.text}
            {c.mode === 'follow' && follow ? (
              follow.on ? (
                <div>Right now this gives: {xdrStateText(follow)}.</div>
              ) : (
                <div className="xdr-follow-off" style={{ color: '#a94442', fontWeight: 600 }}>
                  XDR stays OFF on this device: {followOffReason(follow)}. Choose Always on, or turn XDR on for the company or the group (Settings &gt; Securitee &gt; XDR).
                </div>
              )
            ) : null}
          </div>
        </div>
      ))}
    </Modal>
  )
}

function ProductiviteeDialog({
  deviceId,
  row,
  initialLevel,
  onClose,
  onSaved,
}: {
  deviceId: number
  row: DeviceAgentModuleRow
  initialLevel: number
  onClose(): void
  onSaved(): Promise<void>
}) {
  return (
    <ProductiviteeConsentDialog
      initialLevel={initialLevel}
      onClose={onClose}
      onSave={async (choice) => {
        await saveAgentProductivitee(deviceId, { module: row.id, status: 'premium', ...choice })
        await onSaved()
      }}
    />
  )
}

/**
 * Fereastra de acord Productivitee, impartita cu fila Modules din panoul de
 * selectie multipla. Ea cere nivelul si cele doua acorduri; cine o deschide
 * hotaraste pe ce cap le scrie (`onSave`). O eroare aruncata din `onSave` se
 * arata si lasa fereastra deschisa.
 */
export function ProductiviteeConsentDialog({
  initialLevel,
  intro = 'Choose how much this device records:',
  onClose,
  onSave,
}: {
  initialLevel: number
  intro?: string
  onClose(): void
  onSave(choice: { level: number; consent: boolean; consentExtended: boolean }): Promise<void>
}) {
  const [level, setLevel] = useState<number>(initialLevel)
  const [consent, setConsent] = useState(false)
  const [consentExtended, setConsentExtended] = useState(false)
  const [saving, setSaving] = useState(false)
  const extended = level === PRODUCTIVITEE_LEVEL.extended
  const canSave = consent && (!extended || consentExtended) && !saving

  const save = async () => {
    setSaving(true)
    try {
      await onSave({ level, consent, consentExtended })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Something went wrong. Please try again.'
      toast(message, 'Error', 'error')
      setSaving(false)
    }
  }

  return (
    <Modal
      id="productivitee-consent"
      open
      onClose={onClose}
      title="Enable Productivitee"
      labelledBy="productiviteeConsentLabel"
      staticBackdrop
      portal
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={!canSave}>
            Enable
          </button>
        </>
      }
    >
      <p style={{ marginBottom: 12 }}>{intro}</p>

      <div style={{ marginBottom: 10 }}>
        <SettingsRadio
          name="productivitee_level"
          value={PRODUCTIVITEE_LEVEL.basic}
          checked={!extended}
          onChange={(on) => on && setLevel(PRODUCTIVITEE_LEVEL.basic)}
          label={<strong>Basic</strong>}
        />
        <div style={{ marginLeft: 26, color: '#98a1aa', fontSize: 13 }}>{PRODUCTIVITEE_DRAFT.basic}</div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <SettingsRadio
          name="productivitee_level"
          value={PRODUCTIVITEE_LEVEL.extended}
          checked={extended}
          onChange={(on) => on && setLevel(PRODUCTIVITEE_LEVEL.extended)}
          label={<strong>Extended</strong>}
        />
        <div style={{ marginLeft: 26, color: '#98a1aa', fontSize: 13 }}>{PRODUCTIVITEE_DRAFT.extended}</div>
      </div>

      <div className="note note-warning" style={{ marginBottom: 10 }}>
        {PRODUCTIVITEE_DRAFT.enableNotice}
      </div>
      <div style={{ marginBottom: extended ? 14 : 0 }}>
        <SettingsCheck
          name="productivitee_consent"
          checked={consent}
          onChange={setConsent}
          label="I confirm the above and accept it on behalf of my organization."
        />
      </div>

      {extended ? (
        <>
          <div className="note note-warning" style={{ marginBottom: 10 }}>
            {PRODUCTIVITEE_DRAFT.extendedNotice}
          </div>
          <SettingsCheck
            name="productivitee_consent_extended"
            checked={consentExtended}
            onChange={setConsentExtended}
            label="I confirm the additional Extended collection notice."
          />
        </>
      ) : null}
    </Modal>
  )
}
